import { useTranslation } from "react-i18next";
import { Avatar } from "@/shared/ui/primitives";
import { cn } from "@/shared/lib/cn";
import type { ShellUser } from "../AppShell/useShellUser";

export interface SidebarUserProps {
  user: ShellUser | null;
  collapsed?: boolean;
}

/**
 * El bloque de la persona conectada, al pie de la barra lateral.
 *
 * Es INFORMATIVO, no interactivo: las acciones de la cuenta (cerrar
 * sesión, entrar, registrarse) viven en el menú de la barra superior. Dos
 * sitios distintos para cerrar sesión es una decisión que tomar cada vez
 * y una más que mantener.
 *
 * Fondo de vidrio, no el degradado violeta de las HeroCard: ese es
 * justo el fondo que Anahí descartó.
 *
 * El correo puede ser larguísimo, así que `min-w-0` + `truncate`. Sin el
 * `min-w-0` un ítem flex no baja del ancho de su contenido y el correo
 * ensancharía la barra entera.
 */
export function SidebarUser({ user, collapsed = false }: SidebarUserProps) {
  const { t } = useTranslation();

  if (!user) {
    return (
      <div className={cn("px-3 pb-3", collapsed && "px-2 text-center")}>
        <p className={cn("text-[11px] leading-snug text-ds-ink-3", collapsed && "sr-only")}>
          {t("Sesión no iniciada")}
        </p>
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="group relative flex justify-center px-2 pb-3">
        <Avatar initials={user.initials} size="sm" role="img" aria-label={user.name} />
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute bottom-3 left-full z-50 ml-2 whitespace-nowrap",
            "rounded-xl border border-ds-border bg-white px-2.5 py-1.5 text-[12px] font-semibold text-ds-ink shadow-ds-card",
            "opacity-0 transition-opacity duration-150 motion-reduce:transition-none group-hover:opacity-100",
          )}
        >
          {user.name}
        </span>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3">
      <div className="flex items-center gap-2.5 rounded-2xl border border-ds-border bg-white/50 px-2.5 py-2">
        <Avatar initials={user.initials} size="sm" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12.5px] font-semibold leading-tight text-ds-ink">
            {user.name}
          </span>
          <span className="mt-0.5 block truncate text-[11px] leading-tight text-ds-ink-3">
            {user.email}
          </span>
        </span>
      </div>
    </div>
  );
}
